import * as styles from "./CentreListings.module.scss";

import React, { createRef, useCallback, useEffect, useState } from "react";
import { Button } from "../../../components/common";

import CentreListingCard, {
	type CentreListingCardProps,
} from "../../../components/SignPostCards/CentreListingCard";
import { Sort } from "../../../components/common/Icons";
import { useOurCentres } from "../../../context";
import { Colours } from "../../../utils/colours";
import { haversineDistance } from "../../../utils/distance";
import WrappedSection from "../../WrappedSection/WrappedSection";
import { FiltersButton } from "../DogListingsFilters/DogListingsFilters";
const INITIAL_LOAD = 12;

export const CentreListings: React.FC = () => {
	const [showSortOptions, setShowSortOptions] = useState(false);
	const [centresLoaded, setCentresLoaded] = useState(INITIAL_LOAD);
	const [loadCounter, setLoadCounter] = useState(0);

	const {
		centreLocations,
		centresInRange,
		sortBy,
		onUpdateSort,
		userLocation,
	} = useOurCentres();
	const calculateLoaded = () =>
		`${(centresLoaded / centresInRange?.length) * 100}%`;

	const listBreakRef = createRef<HTMLDivElement>();

	const [centresWithDistance, setCentresWithDistance] = useState<
		CentreListingCardProps[]
	>(
		centreLocations.map((centre) => ({
			centreName: centre.title,
			city: centre.field_city,
			googleMapsUrl: centre.field_google_maps_link,
			imgUrl:
				centre.relationships.field_centre_images?.[0].field_media_image?.uri,
			phoneNumber: centre.field_phone_number,
			postCode: centre.field_postcode,
			url: centre.path.alias,
		})),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: loadCounter is used to trigger a scroll to the top of the list
	useEffect(() => {
		setCentresWithDistance(
			centresInRange.map((centre) => ({
				centreName: centre.title,
				city: centre.field_city,
				googleMapsUrl: centre.field_google_maps_link,
				imgUrl:
					centre.relationships.field_centre_images?.[0].field_media_image?.uri,
				phoneNumber: centre.field_phone_number,
				postCode: centre.field_postcode,
				url: centre.path.alias,
				distance: haversineDistance(
					{
						latitude: Number.parseFloat(centre.field_latitude),
						longitude: Number.parseFloat(centre.field_longitude),
						address: "",
					},
					userLocation,
				),
			})),
		);
	}, [loadCounter, centresInRange, userLocation]);

	const onLoadMore = () => {
		setLoadCounter((prev) => prev + 1);
		setCentresLoaded(
			Math.min(centresLoaded + INITIAL_LOAD, centresInRange?.length),
		);
	};

	const getSortName = () => {
		switch (sortBy) {
			case "Near":
				return "Distance (Closest to Furthest)";
			case "Far":
				return "Distance (Furthest to Closest)";
			case "A":
				return "Alphabetical (A - Z)";
			case "Z":
				return "Alphabetical (Z - A)";
		}
	};

	const getSortOptions = useCallback(() => {
		if (!showSortOptions) return;
		if (userLocation?.latitude)
			return [
				{
					title: "Distance (Closest to Furthest)",
					onClick: () => onUpdateSort("Near"),
				},
				{
					title: "Distance (Furthest to Closest)",
					onClick: () => onUpdateSort("Far"),
				},
				{
					title: "Alphabetical (A - Z)",
					onClick: () => onUpdateSort("A"),
				},
				{
					title: "Alphabetical (Z - A)",
					onClick: () => onUpdateSort("Z"),
				},
			];

		return [
			{
				title: "Alphabetical (A - Z)",
				onClick: () => onUpdateSort("A"),
			},
			{
				title: "Alphabetical (Z - A)",
				onClick: () => onUpdateSort("Z"),
			},
		];
	}, [showSortOptions, userLocation?.latitude, onUpdateSort]);

	const sortCentresData = () => {
		const isCloser = (
			{ distance: distanceA }: CentreListingCardProps,
			{ distance: distanceB }: CentreListingCardProps,
		) => {
			return distanceA > distanceB;
		};
		switch (sortBy) {
			case "Near":
				return (a: CentreListingCardProps, b: CentreListingCardProps) => {
					if (isCloser(a, b)) return 1;
					if (isCloser(b, a)) return -1;
					return 0;
				};
			case "Far":
				return (a: CentreListingCardProps, b: CentreListingCardProps) => {
					if (isCloser(a, b)) return -1;
					if (isCloser(b, a)) return 1;
					return 0;
				};
			case "A":
				return (a: CentreListingCardProps, b: CentreListingCardProps) => {
					if (a.centreName < b.centreName) return -1;
					if (a.centreName > b.centreName) return 1;
					return 0;
				};
			case "Z":
				return (a: CentreListingCardProps, b: CentreListingCardProps) => {
					if (a.centreName < b.centreName) return 1;
					if (a.centreName > b.centreName) return -1;
					return 0;
				};
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: we want sortBy to trigger this effect to hide the sort options
	useEffect(() => {
		setShowSortOptions(false);
	}, [sortBy]);

	return (
		<WrappedSection
			sectionName={"CentreListings"}
			sectionColour={Colours.SANDY_FUR}
		>
			<div id={"centre-listings"} className={styles.centrelistinginner}>
				{centresWithDistance?.length > 0 ? (
					<FiltersButton
						IconLeft={Sort}
						className={styles.sortfilters}
						title={"Sort"}
						value={getSortName()}
						onClick={() => setShowSortOptions((show) => !show)}
						options={getSortOptions()}
					/>
				) : (
					<p>
						Sorry, no results found. Please try expanding your search or viewing
						all results.
					</p>
				)}
				<div className={styles.container}>
					<div className={styles.cards}>
						{centresWithDistance
							?.sort(sortCentresData())
							?.slice(0, centresLoaded)
							.map((centre, i) => {
								if (i > 1 && i % INITIAL_LOAD === 0) {
									return (
										<>
											<div
												ref={listBreakRef}
												key={`count-${centre.centreName}-${i}`}
												className={styles.listbreak}
											/>
											<CentreListingCard key={centre.centreName} {...centre} />
										</>
									);
								}
								return (
									<CentreListingCard key={centre.centreName} {...centre} />
								);
							})}
					</div>
					{centresWithDistance?.length > 0 && (
						<div className={styles.viewmorewrapper}>
							<div className={styles.viewmoretext}>{`Viewed ${Math.min(
								centresLoaded,
								centresInRange?.length,
							)} of ${centresInRange?.length} rehoming centre${
								centresInRange?.length > 1 ? "s" : ""
							}`}</div>
							<div className={styles.loadingbar}>
								<div
									className={styles.loadingbarfiller}
									style={{
										width: calculateLoaded(),
									}}
								/>
							</div>
							{centresLoaded < centresWithDistance?.length && (
								<Button
									variant="tertiary"
									className={styles.showmorebutton}
									onClick={onLoadMore}
								>
									Show More
								</Button>
							)}
						</div>
					)}
				</div>
			</div>
		</WrappedSection>
	);
};

export default CentreListings;
